import moment from 'moment';
import { Asset } from '../models/site-models';
import wellknown from '../../../../lib/wellknown';
import UsageService from './usage.service';
import { dataModel } from '../common/chart/chart.component';
import { ConfirmDelete } from '../../../components/confirm-modal/confirm-modal';
import googleMapsService from '../common/map/google.maps.service';

const Constant = {
  featureKey: 'onei3:feature',
  featureValue: 'site',
  geoZoneKey: 'onei3:geozone',
};

export default class SiteService {
  constructor(
    securityGroupsService,
    EquipmentService,
    timeSeriesAPI,
    $q,
    $ngRedux,
    $filter,
    $uibModal,
    gettext,
    $timeout,
    gettextCatalog,
  ) {
    'ngInject';
    this.securityGroupsService = securityGroupsService;
    this.EquipmentService = EquipmentService;
    this.timeSeriesAPI = timeSeriesAPI;
    this.polygon = undefined;
    this.usageService = new UsageService($q);
    this.reduxStore = $ngRedux;
    this.$q = $q;
    this.$filter = $filter;
    this.views = [];
    this.$uibModal = $uibModal;
    this.gettext = gettext;
    this.$timeout = $timeout;
    this.gettextCatalog = gettextCatalog;

    this.seenIn = {
      seenIn3d: {
        key: 'seenIn3d',
        desc: this.gettext('Seen In 3 Days'),
        color: '#00aef4',
      },
      seenIn7d: {
        key: 'seenIn7d',
        desc: this.gettext('Seen In 7 Days'),
        color: '#41c8ff',
      },
      seenIn14d: {
        key: 'seenIn14d',
        desc: this.gettext('Seen In 14 Days'),
        color: '#8edeff',
      },

      notSeenIn14d: {
        key: 'notSeenIn14d',
        desc: this.gettext('Seen In 14+ Days'),
        color: '#dfe7eb',
      },
      offSite: {
        key: 'offSite',
        desc: this.gettext('Off Site'),
        color: '#ff6c3e',
      },
    };

    this.usedIn = {
      usedIn3d: {
        key: 'usedIn3d',
        desc: this.gettext('Used In 3 Days'),
        color: '#77c176',
      },
      notUsedIn3d: {
        key: 'notUsedIn3d',
        desc: this.gettext('Not Used In 3+ Days'),
        color: '#f8be1c',
      },
      notUsedIn7d: {
        key: 'notUsedIn7d',
        desc: this.gettext('Not Used In 7+ Days'),
        color: '#ff9800',
      },
      notUsedIn14d: {
        key: 'notUsedIn14d',
        desc: this.gettext('Not Used In 14+ Days'),
        color: '#ff6c3e',
      },
    };

    this.onSiteStatusMap = {
      onSite: this.gettext('On Site'),
      LikelyOnSite: this.gettext('Likely On Site'),
      offSite: this.gettext('Off Site'),
    };

    //data
    this.assetsIndexer = undefined;
    this.site = undefined;
    this.onSitePercent = undefined;
    this.totalInventory = undefined;
    this.usageStatus = undefined;
    this.usedInPercent = undefined;
  }

  get assets() {
    return Object.values(this.assetsIndexer || {});
  }

  registerView(view) {
    view.index = this.views.length;
    this.views.push(view);
    return view;
  }

  unRegisterView(view) {
    this.views = this.views.filter((v) => view.index !== view.index);
  }

  updateViews() {
    this.$timeout((_) => {
      this.views.forEach((v) => {
        if (v.updateBindings) {
          v.updateBindings();
        }
      });
    });
  }

  getSites() {
    const siteAttr = { 'onei3:feature': 'site' };
    return this.securityGroupsService.getSecurityGroupsByAttrs(siteAttr).then(({ data }) => {
      const d = data.map((s) => ({
        ...s,
        geoZone: s.attrs['onei3:geozone'] || '',
        description: s.attrs['description'] || '',
        address: s.attrs['address'] || '',
        creationDate: s.createdAt ? this.$filter('TableDateFilter')(s.createdAt) : '',
      }));
      return d;
    });
  }

  getSite(id) {
    this.siteStatus = 'loading';
    if (this.site && this.site.id !== id) {
      this.clearData();
    }
    return this.securityGroupsService.getSingleGroup(id).then(({ data }) => {
      this.site = {
        ...data,
        geoZone: data.attrs['onei3:geozone'] || '',
        description: data.attrs['description'] || '',
        address: data.attrs['address'] || '',
        assets: data.resources.filter((r) => r.type === 'equipment').map((r) => r.id),
      };

      return this.site;
    });
  }

  /**
   * this method calls elastic search for assets, but due to delay in indexing the new added assets
   * we switch to use call be ids instead.
   * @param {*} id
   * @returns
   */
  getSiteAssetsBySiteId(id) {
    const f = {
      attrs: [],
      tags: [{ value: `onei3:security:group:${id}`, group: 'security groups' }],
      queries: ['*'],
      limit: 2500,
      since: 0,
      sort: ['name.raw:asc'],
    };

    return this.EquipmentService.getEquipmentBySearchFilter(f).then(({ data }) => {
      this.assetsIndexer = (data.hits || []).reduce((acc, a) => {
        const asset = new Asset(a);

        if (this.site) {
          asset.onSite = this.isPointInsideGaPolygon(asset.lat, asset.lng, this.site.geoZone);
        }

        asset.seenIn = this.getSeenInKey(asset.lastSeenInDays);
        const { status, color } = this.getOnsiteStatus(asset.seenIn, asset.onSite);
        asset.onSiteStatus = status || '';
        asset.onSiteStatusColor = color || '';
        acc[asset.id] = asset;
        return acc;
      }, {});

      //calculate total matrix
      this.totalInventory = Object.keys(this.assetsIndexer).length;
      this.onSitePercent = this.getOnsitePercent(Object.values(this.assetsIndexer));
      this.usageStatus = 'loading';
      this.siteStatus = 'loaded';

      //update the ui
      this.updateViews();

      //get asset usage data
      this.getAssetUsageSeriesData(Object.keys(this.assetsIndexer));

      return {
        ...data,
        hits: Object.values(this.assetsIndexer),
      };
    });
  }

  getSiteAssetsByIds(assetIds) {
    const filter = {
      criteria: {
        ids: assetIds,
      },
    };
    return this.EquipmentService.postEquipmentListByIds(filter).then(({ data = [] }) => {
      const polygon = this.site ? googleMapsService.getPolygon(this.site.geoZone) : '';
      this.assetsIndexer = (data || []).reduce((acc, a) => {
        const asset = new Asset(a);

        if (this.site) {
          asset.onSite = googleMapsService.isPointInsideGaPolygon(asset.lat, asset.lng, polygon);
        }

        asset.seenIn = this.getSeenInKey(asset.lastSeenInDays);
        const { status, color } = this.getOnsiteStatus(asset.seenIn, asset.onSite);
        asset.onSiteStatus = status || '';
        asset.onSiteStatusColor = color || '';
        acc[asset.id] = asset;
        return acc;
      }, {});

      //calculate total matrix
      this.totalInventory = this.assets.length;
      this.onSitePercent = this.getOnsitePercent(this.assets);
      this.usageStatus = 'loading';
      this.siteStatus = 'loaded';

      //update the ui
      this.updateViews();

      //get asset usage data
      this.getAssetUsageSeriesData(Object.keys(this.assetsIndexer));
      return this.assets;
    });
  }

  /**
   * calculation using leaflet
   * @param {*} lat
   * @param {*} lng
   * @param {*} geometry
   * @returns
   */
  isPointInsidePolygon(lat, lng, geometry) {
    if (!lat || !lng) return true;
    const [points] = (wellknown.parse(geometry) || {}).coordinates;
    if (!points || !points.length) return true;
    const polyPoints = (points || []).map(([lat, lng]) => L.latLng(lat, lng));

    const x = lat;
    const y = lng;

    let inside = false;
    for (let i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
      const xi = polyPoints[i].lat,
        yi = polyPoints[i].lng;
      const xj = polyPoints[j].lat,
        yj = polyPoints[j].lng;

      const intersect = yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  }

  getAssetUsageSeriesData(assetIds = []) {
    this.assetSeries = undefined;
    return this.timeSeriesAPI.getAssetSeries(assetIds).then((series) => {
      return this.usageService
        .getUsageSeriesForAssets(series, assetIds)
        .then(({ assetsUsageSeries, seriesIds }) => {
          // No need for this at this time, so far will not doing aggregation by day usage.
          // this.getAssetSeriesData(seriesIds);
          this.assetSeries = assetsUsageSeries;
          //update assetsIndexer usage data
          Object.keys(this.assetsIndexer).forEach((assetId) => {
            const usage = assetsUsageSeries[assetId];
            this.assetsIndexer[assetId].usage = usage;
            this.assetsIndexer[assetId].usedAtInDays = usage?.usedAtInDays;
            this.assetsIndexer[assetId].lastReportedUsageIn3d =
              (usage?.lastReportedInDays || Number.MAX_VALUE) < 3.5;
            const usageInDays = usage ? usage.usedAtInDays : Number.MAX_VALUE;
            this.assetsIndexer[assetId].usedIn = this.getUsedInInKey(usageInDays);
            this.assetsIndexer[assetId].usedAt = usage?.usedAt;
          });
          this.usedInPercent = this.getUsedInPercent(this.assets);
          this.usageStatus = 'loaded';
          this.updateViews();

          this.updateAssetsWithLikeQtyData()
            .then((_) => {
              this.updateViews();
            })
            .catch((e) => {
              console.error(e);
            });

          return this.assetSeries;
        });
    });
  }

  getAssetSeriesData(seriesIds = {}) {
    //start from the beginning of next day in order to include aggregated current day in the result
    const end = moment().startOf('day').add(1, 'day').toDate();

    // in order to keep the range relevant to 1 day aggregation: by using end date to construct of
    //and subtract by hour instead of day to keep the utc date range hour matching
    const start = moment(end)
      .subtract(28 * 24, 'hours')
      .toDate();

    console.log(moment(start).format());
    console.log(moment(end).format());

    const dateRange = `${start.toISOString()}/${end.toISOString()}`;
    this.timeSeriesAPI
      .getSeriesData(dateRange, seriesIds, ['delta', 'first', 'last', 'count'], '1day')
      .then((data) => {
        return this.usageService.aggregateDataPerPeriod(data).then((d) => console.log(d));
      });
  }

  getLastSeenChartData(assets = []) {
    return this.$q((resolve) => {
      const assetIds = (assets || []).map((s) => s.id);
      if (!this.assets) resolve(undefined);
      const seenIn3d = [];
      const seenIn7d = [];
      const seenIn14d = [];
      const notSeenIn14d = [];
      const offSite = [];
      assetIds.forEach((assetId) => {
        const asset = this.assetsIndexer[assetId];
        if (asset.onSite) {
          switch (asset.seenIn) {
            case this.seenIn.seenIn3d.key:
              seenIn3d.push(asset.id);
              break;
            case this.seenIn.seenIn7d.key:
              seenIn7d.push(asset.id);
              break;
            case this.seenIn.seenIn14d.key:
              seenIn14d.push(asset.id);
              break;
            default:
              notSeenIn14d.push(asset.id);
              break;
          }
        } else {
          offSite.push(asset.id);
        }
      });
      this.lastSeenChartData = [
        new dataModel(
          this.seenIn.seenIn3d.key,
          this.seenIn.seenIn3d.desc,
          this.seenIn.seenIn3d.color,
          seenIn3d.length,
        ),
        new dataModel(
          this.seenIn.seenIn7d.key,
          this.seenIn.seenIn7d.desc,
          this.seenIn.seenIn7d.color,
          seenIn7d.length,
        ),
        new dataModel(
          this.seenIn.seenIn14d.key,
          this.seenIn.seenIn14d.desc,
          this.seenIn.seenIn14d.color,
          seenIn14d.length,
        ),
        new dataModel(
          this.seenIn.notSeenIn14d.key,
          this.seenIn.notSeenIn14d.desc,
          this.seenIn.notSeenIn14d.color,
          notSeenIn14d.length,
        ),
        new dataModel(
          this.seenIn.offSite.key,
          this.seenIn.offSite.desc,
          this.seenIn.offSite.color,
          offSite.length,
        ),
      ];

      resolve(this.lastSeenChartData.filter((i) => i.value));
    });
  }

  getUsageChartData(assets) {
    const assetIds = (assets || []).map((s) => s.id);

    return this.$q((resolve) => {
      if (!assetIds.length) resolve(undefined);
      const usedIn3d = [];
      const notUsedIn3d = [];
      const notUsedIn7d = [];
      const notUsedIn14d = [];
      assetIds.forEach((assetId) => {
        const asset = this.assetsIndexer[assetId];
        switch (asset.usedIn) {
          case this.usedIn.usedIn3d.key:
            usedIn3d.push(assetId);
            break;
          case this.usedIn.notUsedIn3d.key:
            notUsedIn3d.push(assetId);
            break;
          case this.usedIn.notUsedIn7d.key:
            notUsedIn7d.push(assetId);
            break;
          default:
            notUsedIn14d.push(assetId);
            break;
        }
      });
      this.usageChartData = [
        new dataModel(
          this.usedIn.usedIn3d.key,
          this.usedIn.usedIn3d.desc,
          this.usedIn.usedIn3d.color,
          usedIn3d.length,
        ),
        new dataModel(
          this.usedIn.notUsedIn3d.key,
          this.usedIn.notUsedIn3d.desc,
          this.usedIn.notUsedIn3d.color,
          notUsedIn3d.length,
        ),
        new dataModel(
          this.usedIn.notUsedIn7d.key,
          this.usedIn.notUsedIn7d.desc,
          this.usedIn.notUsedIn7d.color,
          notUsedIn7d.length,
        ),
        new dataModel(
          this.usedIn.notUsedIn14d.key,
          this.usedIn.notUsedIn14d.desc,
          this.usedIn.notUsedIn14d.color,
          notUsedIn14d.length,
        ),
      ];
      resolve(this.usageChartData.filter((i) => i.value));
    });
  }

  clearData() {
    this.assetSeries = undefined;
    this.assetsIndexer = {};
    this.onSitePercent = undefined;
    this.totalInventory = undefined;
    this.usageStatus = undefined;
    this.usedInPercent = undefined;
    this.updateViews();
  }

  getAssetsByFilter(query, filters, preDefinedFilter) {
    return this.$q((resolve) => {
      let assets = Object.values(this.assetsIndexer || {});
      if (preDefinedFilter && preDefinedFilter.match) {
        assets = assets.filter((a) => preDefinedFilter.match(a));
      }
      resolve(this.filterAssets(assets, query, filters));
    });
  }

  getOnsitePercent(assets = []) {
    const onSiteTotal = assets.filter((a) => a.onSite).length;
    return assets.length ? ((onSiteTotal * 100) / assets.length).toPrecision(3) : 0;
  }

  getSeenInKey(seenInDays) {
    //last seen col in the ui use moment().fromNow(); witch round up the date like ( 3.5 will be 4 days ago ), so to keep the data consistent we will use .5 as the limit
    switch (true) {
      case seenInDays < 3.5:
        return this.seenIn.seenIn3d.key;
      case seenInDays < 7.5:
        return this.seenIn.seenIn7d.key;
      case seenInDays < 14.5:
        return this.seenIn.seenIn14d.key;
      default:
        return this.seenIn.notSeenIn14d.key;
    }
  }

  getUsedInInKey(usageInDays) {
    //last used col in the ui use moment().fromNow(); witch round up the date like ( 3.5 will say 4 days ago), so to keep the data consistent we will use .5 as the limit
    switch (true) {
      case usageInDays < 3.5:
        return this.usedIn.usedIn3d.key;
      case usageInDays < 7.5:
        return this.usedIn.notUsedIn3d.key;
      case usageInDays < 14.5:
        return this.usedIn.notUsedIn7d.key;
      default:
        return this.usedIn.notUsedIn14d.key;
    }
  }

  filterAssets(assets, query, filters) {
    let assetsRet = assets;

    if (query) {
      assetsRet = this.$filter('filter')(assetsRet, (query || '').trim());
    }

    return assetsRet.filter((asset) => {
      return filters.every((filter) => filter.match(asset));
    });
  }

  getCsvData(assets, columnsDef = []) {
    return this.$q((resolve, reject) => {
      if (!columnsDef.length) reject('missing columns definition');

      const headers = columnsDef.reduce((csvHeader, col) => {
        csvHeader[col.field] = col.title;
        return csvHeader;
      }, {});

      const headersKeyArray = Object.keys(headers || {});

      const data = assets.map((asset) => {
        const csvRowData = headersKeyArray.reduce((row, field) => {
          //this ok as long we have asset as flat object
          // else we need to get the value using lodash get or the column getvalue()
          row[field] = asset[field];
          return row;
        }, {});
        return csvRowData;
      });

      resolve([headers, ...data]);
    });
  }

  getUsedInPercent(assets = []) {
    const usedInTotal = assets.filter((a) => a.usedIn === this.usedIn.usedIn3d.key && a.onSite)
      .length;
    return assets.length ? ((usedInTotal * 100) / assets.length).toPrecision(3) : 0;
  }

  updateAssetsWithLikeQtyData() {
    return this.$q((resolve) => {
      const assetWithQty = this.calculateLikeQtyOnsite(this.assets);
      Object.keys(assetWithQty).forEach((assetId) => {
        const { qty, qtyPercent } = assetWithQty[assetId];
        this.assetsIndexer[assetId].ccLikeQtyOnSite = qty;
        this.assetsIndexer[assetId].ccLikeQtyOnSiteUsagePercent = qtyPercent;
      });
      resolve(this.assetsIndexer);
    });
  }

  /**
   *
   * @param {*} assets
   * @returns object {assetId : {qty,qtyPercent}}
   */
  calculateLikeQtyOnsite(assets = []) {
    //to group assets that onsite by catClass
    const catClassOnsite = {};
    //to group assets that onsite and used in 7 days by catClass
    const catClassOnsiteUsedIn7days = {};
    assets.forEach((asset) => {
      if (asset.onSite) {
        const catClass = asset.catClass;
        if (catClass) {
          catClassOnsite[catClass] = [asset.id, ...(catClassOnsite[catClass] || [])];

          if (asset.usedIn === this.usedIn.usedIn3d.key) {
            catClassOnsiteUsedIn7days[catClass] = [
              asset.id,
              ...(catClassOnsiteUsedIn7days[catClass] || []),
            ];
          }
        }
      }
    });

    const assetsWithLikeQtyData = Object.keys(catClassOnsite).reduce(
      (assetsWithLikeQty, catClass) => {
        //as long as there is catClass then there should be at least one asset
        const assetsOnSitQty = catClassOnsite[catClass];
        const assetsOnSitQtyUsed = catClassOnsiteUsedIn7days[catClass] || [];
        const usedQtyPercent = (
          (assetsOnSitQtyUsed.length * 100) /
          assetsOnSitQty.length
        ).toPrecision(3);

        assetsOnSitQty.forEach((assetId) => {
          assetsWithLikeQty[assetId] = {
            qty: assetsOnSitQty.length,
            qtyPercent: usedQtyPercent,
          };
        });

        return assetsWithLikeQty;
      },
      {},
    );

    return assetsWithLikeQtyData;
  }

  getOnsiteStatus(seenIn, onsite) {
    if (!seenIn) return {};
    if (!onsite) return { status: this.onSiteStatusMap.offSite, color: this.seenIn.offSite.color };
    if (seenIn === this.seenIn.seenIn3d.key)
      return { status: this.onSiteStatusMap.onSite, color: this.seenIn.seenIn3d.color };
    return { status: this.onSiteStatusMap.LikelyOnSite, color: this.seenIn[seenIn].color };
  }

  createSite(site, assetsIds, users) {
    const { name, description, address, assets, geoZone } = site || {};
    const attrF = { [Constant.featureKey]: Constant.featureValue };
    const attrG = geoZone ? { [Constant.geoZoneKey]: geoZone } : {};
    const newSec = {
      name,
      attrs: {
        ...attrF,
        ...attrG,
        description,
        address,
      },
    };
    const prefix = this.securityGroupsService.getResourceUrl();
    const assetsResources = (assetsIds || []).map((id) => ({
      op: 'add',
      value: {
        resource: `${prefix}${id}`,
      },
    }));

    const userPrefix = this.securityGroupsService.getUserUrl();
    const usersResources = (users || []).map((user) => ({
      op: 'add',
      value: {
        user: `${userPrefix}${user.id}`,
      },
    }));

    return this.createNewSec(newSec).then((sec) => {
      return this.$q((resolve) => {
        //update assets / users
        this.securityGroupsService
          .patchResources(sec.id, [...assetsResources, ...usersResources])
          .finally(() => {
            resolve(sec);
          });
      });
    });
  }

  updateSiteAssets(securityGroupId, addedAssets = [], removedAssets = []) {
    const assetsResources = [];
    const prefix = this.securityGroupsService.getResourceUrl();

    addedAssets.forEach((id) => {
      assetsResources.push({
        op: 'add',
        value: {
          resource: `${prefix}${id}`,
        },
      });
    });

    removedAssets.forEach((id) => {
      assetsResources.push({
        op: 'remove',
        value: {
          resource: `${prefix}${id}`,
        },
      });
    });

    return this.securityGroupsService.patchResources(securityGroupId, assetsResources);
  }

  createNewSec(sec) {
    return this.securityGroupsService.postNewGroup(sec).then((res) => res.data);
  }

  openAssetSelector(selected = []) {
    return this.$q((resolve) => {
      this.$uibModal
        .open({
          animation: true,
          template: require('../../../components/assetpicker/asset.multi.picker.html'),
          controller: 'AssetMultiPickerController',
          size: 'lg',
          resolve: {
            singleSelect: false,
            requiresSelection: false,
            maxSelection: () => 0,
            selectedAssets: () =>
              selected.reduce((idsIndex, asset) => {
                idsIndex[asset?.id] = asset || {};
                return idsIndex;
              }, {}),
          },
        })
        .result.then((res) => {
          resolve(res);
        })
        .catch(() => {
          resolve(selected);
        });
    });
  }

  updateSite(site) {
    const { id, name, attrs, description, address, geoZone } = site || {};
    const attrG = { [Constant.geoZoneKey]: geoZone };
    const sec = {
      name,
      attrs: {
        ...attrs,
        ...attrG,
        description,
        address,
      },
    };
    return this.securityGroupsService.putGroupUpdate(id, sec);
  }

  deleteSite(site) {
    const onConfirm = () => this.securityGroupsService.deleteGroup(site.id);
    const title = this.gettextCatalog.getString('Delete Site');
    const msg = this.gettextCatalog.getString('Are you sure you want to delete "{{name}}"?', {
      name: site.name,
    });
    const messages = [msg];
    return ConfirmDelete({ $uibModal: this.$uibModal, title, messages, onConfirm });
  }

  openUsersSelector(selected = [], appliedUsers = []) {
    return this.$uibModal
      .open({
        animation: true,
        template: require('../../../pages/admin/security-groups/view/users/users-modal/users.modal.html'),
        controller: 'SecurityGroupViewUserSelectionModalController',
        size: 'lg',
        resolve: {
          singleSelect: false,
          selectedUsers: () =>
            selected.reduce((idsIndex, user) => {
              idsIndex[user?.id] = user || {};
              return idsIndex;
            }, {}),
        },
      })
      .result.then((res) => {
        const finalSelection = Object.keys(res || {});
        const alreadyAdded = (appliedUsers || []).map((u) => u.id);
        const added = finalSelection.filter((uid) => !alreadyAdded.includes(uid));
        const removed = alreadyAdded.filter((uid) => !finalSelection.includes(uid));
        const finalUsers = Object.values(res || {});
        return {
          added,
          removed,
          finalUsers,
        };
      });
  }

  updateSiteUsers(siteId, addedIds = [], removedIds = []) {
    const prefix = this.securityGroupsService.getUserUrl();

    const addedUsersResources = (addedIds || []).map((id) => ({
      op: 'add',
      value: {
        user: `${prefix}${id}`,
      },
    }));
    const removedUsersResources = (removedIds || []).map((id) => ({
      op: 'remove',
      value: {
        user: `${prefix}${id}`,
      },
    }));

    const usersResources = [...addedUsersResources, ...removedUsersResources];

    return this.securityGroupsService.patchResources(siteId, usersResources);
  }

  updateSiteAssets(siteId, addedIds = [], removedIds = []) {
    const prefix = this.securityGroupsService.getResourceUrl();

    const addedAssetsResources = (addedIds || []).map((id) => ({
      op: 'add',
      value: {
        resource: `${prefix}${id}`,
      },
    }));
    const removedAssetsResources = (removedIds || []).map((id) => ({
      op: 'remove',
      value: {
        resource: `${prefix}${id}`,
      },
    }));

    const resources = [...addedAssetsResources, ...removedAssetsResources];

    return this.securityGroupsService.patchResources(siteId, resources);
  }
}
